import React, { useEffect, useRef } from 'react';
import './About.css';

/* Single ref on the page wrapper watches ALL .reveal children */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('revealed');
            obs.unobserve(e.target);
          }
        }),
      { threshold: 0.1 }
    );
    ref.current.querySelectorAll('.reveal').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
  return ref;
}

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.3.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M3.18 23.4c.3.16.63.19.96.07l13.5-7.79L3 7.8 2.82 21.7c-.01.67.12 1.36.36 1.7zm1.32-17.7L17.46 13 4.5 5.4V5.7zm14.1 6.9l-2.58-1.49-2.58 1.49 2.58 4.47 2.58-4.47z" />
  </svg>
);

const values = [
  { emoji: '🌍', title: 'Real Life First', desc: 'Every feature is designed to move you off the screen and into the world with people you care about.' },
  { emoji: '🔥', title: 'Spontaneity', desc: "The best moments are unplanned. FlareChat makes spontaneous hangouts as easy as sending a text." },
  { emoji: '🔒', title: 'Privacy by Design', desc: "You control who sees your flares and activity. We do not sell your data — ever." },
  { emoji: '❤️', title: 'Community', desc: "We're a small, passionate team that genuinely cares about the people who use FlareChat." },
];

const stats = [
  { value: '100%', label: 'Focus on IRL connection', sub: 'Every feature serves one goal' },
  { value: '0', label: 'Ads or promoted posts', sub: 'Your feed is yours alone' },
  { value: '48h', label: 'Max support response time', sub: 'Real humans, real help' },
  { value: '2026', label: 'Founded', sub: 'East Greenbush, NY' },
];

export default function About() {
  const pageRef = useReveal();

  return (
    <div className="about" ref={pageRef}>

      {/* HERO */}
      <section className="about-hero">
        <div className="about-hero-orb orb-a" />
        <div className="about-hero-orb orb-b" />
        <div className="container">
          <div className="section-tag animate-fade-up" style={{ background: 'rgba(244,97,26,0.2)', color: '#FFAD6F', borderColor: 'rgba(244,97,26,0.35)' }}>
            <span className="dot" style={{ background: '#F5A623' }} />
            Our Story
          </div>
          <h1 className="about-heading animate-fade-up delay-1">
            We built FlareChat<br />because <span className="gradient-text">we were tired</span><br />of scrolling.
          </h1>
          <p className="about-sub animate-fade-up delay-2">
            Technology has been built to keep you scrolling, not to help you spend more time with the people you love. That's because endless scrolling is more profitable than getting people together.
          </p>
        </div>
      </section>

      {/* MISSION */}
      <section className="mission">
        <div className="container">
          <div className="mission-grid">
            <div className="mission-text reveal">
              <div className="section-tag">
                <span className="dot" />
                Our Mission
              </div>
              <h2>One Goal.<br /><span className="gradient-text">More Time Together.</span></h2>
              <p>
                FlareChat is different. Our singular focus is to connect you in the real world with the people who matter most. Every feature, every decision, is built around that one goal: <strong>helping you spend more time together offline.</strong>
              </p>
              <p>
                We started FlareChat because we noticed something troubling — our phones were supposed to make us more connected, but they were actually keeping us apart. Social media optimized for engagement, not for real-life connection.
              </p>
              <p>So we built something different. Something simple. Something that actually works.</p>
            </div>

            <div className="mission-visual reveal delay-2">
              <div className="about-phone-wrap">
                <img src="/images/screen2.jpg" alt="FlareChat Login" className="about-phone" />
                <div className="about-badge badge-a"><span>🎉</span><span>Real IRL Plans</span></div>
                <div className="about-badge badge-b"><span>❤️</span><span>Zero Algorithms</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="values">
        <div className="container">
          <div className="section-header reveal">
            <div className="section-tag">
              <span className="dot" />
              What We Believe
            </div>
            <h2>Our <span className="gradient-text">Core Values</span></h2>
            <p>These principles guide every product decision we make.</p>
          </div>
          <div className="values-grid">
            {values.map((v, i) => (
              <div key={i} className="value-card reveal" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="value-emoji">{v.emoji}</div>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS — dark section */}
      <section className="about-stats">
        <div className="about-stats-bg" />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="section-header reveal">
            <div className="section-tag" style={{ background: 'rgba(245,166,35,0.2)', color: '#F5A623', borderColor: 'rgba(245,166,35,0.3)' }}>
              <span className="dot" style={{ background: '#F5A623' }} />
              By the Numbers
            </div>
            <h2 style={{ color: 'white' }}>Built with <span className="gradient-text">purpose</span></h2>
            <p style={{ color: 'rgba(255,255,255,0.6)' }}>No vanity metrics. Just a product that does what it says.</p>
          </div>
          <div className="stats-grid">
            {stats.map((s, i) => (
              <div key={i} className="stat-card reveal" style={{ transitionDelay: `${i * 0.12}s` }}>
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
                <div className="stat-sub">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DOWNLOAD CTA */}
      <section className="about-cta">
        <div className="about-cta-orb" />
        <div className="container">
          <div className="about-cta-inner reveal">
            <div className="cta-screens">
              <img src="/images/screen3.jpg" alt="FlareChat Feed" className="cta-phone cta-phone-back" />
              <img src="/images/screen5.jpg" alt="FlareChat Chat" className="cta-phone cta-phone-front" />
            </div>
            <div className="cta-text">
              <div className="section-tag">
                <span className="dot" />
                Get the App
              </div>
              <h2>Ready to hang out<br /><span className="gradient-text">in real life?</span></h2>
              <p>
                Join the FlareChat community. Shoot up a flare, see who's free, and make plans with the people who matter most — free on iOS and Android.
              </p>
              <div className="cta-store-row">
                <a href="https://apps.apple.com" target="_blank" rel="noopener noreferrer" className="store-badge">
                  <AppleIcon />
                  <span className="store-badge-text">
                    <span>Download on the</span>
                    <span>App Store</span>
                  </span>
                </a>
                <a href="https://play.google.com" target="_blank" rel="noopener noreferrer" className="store-badge">
                  <PlayIcon />
                  <span className="store-badge-text">
                    <span>Get it on</span>
                    <span>Google Play</span>
                  </span>
                </a>
              </div>
              <p className="cta-note">Free to download · No ads · No endless scrolling</p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
